import * as React from "react"
import { useTranslation } from "@nova/use-translation"
import * as Icons from "@nova/component-icons"
import { ButtonBorderless } from "@nova/component-btn"
import classNames from "classnames"
import { zIndex } from "@nova/constants"

import type { JSX } from "react"

export const NovaModalContainer = ({
    children,
    className,
    title,
    icon: IconComponent,
    isDestructive,
    bottomBar,
    HeaderRowComponent,
    hideTitleUnderline,
    titleMarginClassName = "mt-4",
    closeModal,
    onReturn,
    onCancel,
    ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
    title: string
    icon?: Icons.IconComponent
    className?: string
    isDestructive?: boolean
    bottomBar?: JSX.Element
    HeaderRowComponent?: JSX.Element
    hideTitleUnderline?: boolean
    titleMarginClassName?: string
    closeModal?: () => void
    onReturn?: () => void
    onCancel?: () => void
}) => {
    const { t } = useTranslation()
    React.useEffect(() => {
        const closeModalOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                if (!!onCancel) onCancel()
                else closeModal?.()
            }
        }
        document.addEventListener("keydown", closeModalOnEscape, { once: true })
        return () => {
            document.removeEventListener("keydown", closeModalOnEscape)
        }
    }, [onCancel, closeModal])

    return (
        <div
            {...rest}
            className={classNames(
                "my-auto mx-8 bg-gray-900 rounded-2xl border shadow-elevation-5 flex flex-col",
                { "border-gray-700": !isDestructive },
                { "border-red-700": isDestructive },
                className
            )}
            style={{ minWidth: "21.75rem" }}
        >
            {/* Header & Body */}
            <div className="text-gray-0 pt-2 mb-8 px-8 relative z-10 shrink min-h-0 flex flex-col break-words">
                <div
                    className={classNames("pb-2 mb-2", titleMarginClassName, {
                        "border-b border-gray-700": !hideTitleUnderline,
                    })}
                >
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <div className="flex gap-3">
                                {!!onReturn ? (
                                    <ButtonBorderless
                                        title={title}
                                        LeftIcon={Icons.ArrowLeft}
                                        onClick={onReturn}
                                        className="pl-0"
                                    />
                                ) : (
                                    <>
                                        {IconComponent && (
                                            <IconComponent className="text-gray-100 mb-px" />
                                        )}
                                        <h3 className="select-none">{t(title)}</h3>
                                    </>
                                )}
                            </div>

                            {!!HeaderRowComponent && HeaderRowComponent}
                        </div>

                        {closeModal && (
                            <Icons.SmallIcon
                                className={"text-gray-100 cursor-pointer hover:text-white-700"}
                                Icon={Icons.XClose}
                                onClick={closeModal}
                            />
                        )}
                    </div>
                </div>
                {children}
            </div>

            {/* Footer */}
            {!!bottomBar && (
                <div className="bg-gray-800 w-full flex items-center rounded-b-2xl relative px-4 py-3">
                    {bottomBar}
                </div>
            )}
        </div>
    )
}

export const HighPriorityNovaModalContainer = ({
    className,
    bottomBar,
    closeModal,
    canCloseModal = true,
    icon,
    HeaderRowComponent,
    ...rest
}: React.HTMLAttributes<HTMLDivElement> & {
    title: string
    icon?: Icons.IconComponent
    isDestructive?: boolean
    bottomBar?: JSX.Element
    /** Component will be place to the right of the title in a flex row. */
    HeaderRowComponent?: JSX.Element
    hideTitleUnderline?: boolean
    titleMarginClassName?: string
    closeModal?: () => void
    canCloseModal?: boolean
    onReturn?: () => void
    onCancel?: () => void
}) => {
    return (
        <div
            className="w-[100vw] h-[100vh] fixed inset-0 overflow-hidden flex flex-col items-center justify-end"
            style={{ zIndex: zIndex.HighPriorityModal }}
        >
            <div
                className="fixed overflow-hidden opacity-75 h-full w-full z-40 bg-black-600"
                onClick={canCloseModal ? closeModal : undefined}
            />
            <NovaModalContainer
                className={classNames("relative z-50", className)}
                closeModal={canCloseModal ? closeModal : undefined}
                bottomBar={bottomBar}
                HeaderRowComponent={HeaderRowComponent}
                icon={icon}
                {...rest}
            />
        </div>
    )
}
